import {
  Bot,
  Brain,
  CheckCircle2,
  CircleDot,
  Cloud,
  Database,
  DoorOpen,
  FileSearch,
  Fingerprint,
  LockKeyhole,
  MessageSquareText,
  Monitor,
  Network,
  Route,
  ShieldCheck,
  Sparkles,
  UserRound,
  WandSparkles,
  Zap
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Badge, Card, PageHeader } from "@/components/primitives";
import apiGatewayIcon from "@/assets/aws/api-gateway.svg?url";
import athenaIcon from "@/assets/aws/athena.svg?url";
import bedrockIcon from "@/assets/aws/bedrock.svg?url";
import cloudwatchIcon from "@/assets/aws/cloudwatch.svg?url";
import dynamoDbIcon from "@/assets/aws/dynamodb.svg?url";
import ecrIcon from "@/assets/aws/ecr.svg?url";
import eksIcon from "@/assets/aws/eks.svg?url";
import glueIcon from "@/assets/aws/glue.svg?url";
import iamIcon from "@/assets/aws/iam.svg?url";
import lakeFormationIcon from "@/assets/aws/lake-formation.svg?url";
import rdsIcon from "@/assets/aws/rds.svg?url";
import s3Icon from "@/assets/aws/s3.svg?url";
import stepFunctionsIcon from "@/assets/aws/step-functions.svg?url";

const services = [
  {
    name: "Amazon S3",
    icon: s3Icon,
    title: "The warehouse",
    role: "Stores the raw and cleaned learning events Cosmos can reason over.",
    analogy: "Like a giant filing cabinet that never forgets.",
    usedFor: "Data lake buckets, bronze/silver/gold files, Athena results"
  },
  {
    name: "AWS Glue",
    icon: glueIcon,
    title: "The librarian",
    role: "Keeps the catalog that tells Cosmos what data exists and where to find it.",
    analogy: "Like index cards in a library, but for data.",
    usedFor: "Glue catalog database and Iceberg table metadata"
  },
  {
    name: "Amazon Athena",
    icon: athenaIcon,
    title: "The question asker",
    role: "Lets Cosmos ask SQL questions over the data lake without running a big database.",
    analogy: "Like asking a librarian to inspect the shelves only when you need an answer.",
    usedFor: "Read-only analytics queries and Iceberg table publishing"
  },
  {
    name: "AWS Lake Formation",
    icon: lakeFormationIcon,
    title: "The bouncer",
    role: "Controls who is allowed to read which parts of the data lake.",
    analogy: "Like a VIP list for sensitive learning data.",
    usedFor: "Data location and Glue table permissions"
  },
  {
    name: "Amazon Bedrock",
    icon: bedrockIcon,
    title: "The brain",
    role: "Turns retrieved context into a clear answer and can pull grounded knowledge from a Knowledge Base.",
    analogy: "Like a smart colleague who explains the messy notes in normal language.",
    usedFor: "Live model answers, model routing, Knowledge Base retrieval"
  },
  {
    name: "Amazon EKS",
    icon: eksIcon,
    title: "The kitchen",
    role: "Runs the Cosmos API containers in the AIHR Kubernetes platform.",
    analogy: "Like the professional kitchen where all the Cosmos work gets cooked.",
    usedFor: "Private runtime for API, workers, and MCP-backed capabilities"
  },
  {
    name: "Amazon ECR",
    icon: ecrIcon,
    title: "The recipe shelf",
    role: "Stores the Docker image that gets deployed into the runtime.",
    analogy: "Like keeping the exact recipe version before the kitchen cooks from it.",
    usedFor: "Cosmos API image repository and lifecycle policy"
  },
  {
    name: "Amazon API Gateway + VPC Link",
    icon: apiGatewayIcon,
    title: "The front door",
    role: "Gives people and systems a controlled HTTPS entrance into the private Cosmos runtime.",
    analogy: "Like reception: it checks the visitor and sends them to the right room.",
    usedFor: "HTTP API, private VPC Link, access logs"
  },
  {
    name: "AWS IAM",
    icon: iamIcon,
    title: "The keychain",
    role: "Defines what each part of Cosmos is allowed to touch.",
    analogy: "Like labeled keys where every key opens only the doors it should.",
    usedFor: "IRSA runtime role, S3/Athena/Glue/Bedrock permissions"
  },
  {
    name: "Amazon DynamoDB",
    icon: dynamoDbIcon,
    title: "The sticky-note drawer",
    role: "Optionally stores action requests, user memory, and audit records.",
    analogy: "Like a fast drawer for notes Cosmos needs to remember instantly.",
    usedFor: "Actions, audit trail, and memory persistence"
  },
  {
    name: "Amazon RDS (PostgreSQL)",
    icon: rdsIcon,
    title: "The catalog cabinet",
    role: "Lets Cosmos read LMS course and lesson information from a safe database source.",
    analogy: "Like checking the official course binder without interrupting the classroom.",
    usedFor: "Lesson source and consultant content source"
  },
  {
    name: "AWS Step Functions",
    icon: stepFunctionsIcon,
    title: "The coordinator",
    role: "Can orchestrate post-processing steps after lakehouse data is prepared.",
    analogy: "Like a checklist that makes sure every prep step happens in order.",
    usedFor: "Optional post-processing orchestration hook"
  },
  {
    name: "Amazon CloudWatch",
    icon: cloudwatchIcon,
    title: "The flight recorder",
    role: "Collects logs, dashboards, and alarms when ingestion or materialization needs attention.",
    analogy: "Like the dashboard lights in a car.",
    usedFor: "Runtime logs, API access logs, alarms, observability dashboard"
  }
] as const;

const questionJourney = [
  {
    title: "You ask in plain English",
    text: "Example: \"Which courses should I assign to a new HR director?\" No special query language needed.",
    icons: [],
    icon: MessageSquareText
  },
  {
    title: "Cosmos figures out what you mean",
    text: "It turns the question into intent: courses, journeys, people, approvals, or analytics.",
    icons: [bedrockIcon],
    icon: Brain
  },
  {
    title: "Cosmos gathers the facts",
    text: "It checks the data lake, the LMS catalog, and relevant knowledge sources before answering.",
    icons: [athenaIcon, s3Icon, glueIcon, rdsIcon],
    icon: FileSearch
  },
  {
    title: "The AI writes the answer",
    text: "Bedrock gets the facts and writes a normal, useful answer with sources.",
    icons: [bedrockIcon],
    icon: WandSparkles
  },
  {
    title: "Actions wait for a human",
    text: "If the answer would change something, like assigning a journey, Cosmos stores a request and waits for approval.",
    icons: [dynamoDbIcon, iamIcon, cloudwatchIcon],
    icon: ShieldCheck
  }
] as const;

const saraWalkthrough: Array<{
  service: string;
  text: string;
  image?: string;
  icon?: LucideIcon;
}> = [
  {
    service: "UI",
    icon: Monitor,
    text: "Sara's manager opens Cosmos and asks: \"Build me a 4-week onboarding journey for a new HR Director.\""
  },
  {
    service: "API Gateway",
    image: apiGatewayIcon,
    text: "The request enters through the front door, which forwards it privately to the Cosmos app."
  },
  {
    service: "EKS",
    image: eksIcon,
    text: "The Cosmos API runs in the platform cluster and decides which tools it needs."
  },
  {
    service: "IAM",
    image: iamIcon,
    text: "Cosmos uses tightly scoped permissions so it only reads and writes what it is allowed to."
  },
  {
    service: "S3, Glue, Athena, Lake Formation",
    image: athenaIcon,
    text: "It asks the learning data lake for relevant events, entitlements, and journey signals."
  },
  {
    service: "RDS + Bedrock",
    image: bedrockIcon,
    text: "It combines course catalog facts with Bedrock's plain-language reasoning to draft the journey."
  },
  {
    service: "DynamoDB",
    image: dynamoDbIcon,
    text: "When the manager clicks assign, Cosmos saves a pending action instead of changing LMS immediately."
  },
  {
    service: "CloudWatch",
    image: cloudwatchIcon,
    text: "After an admin approves, Cosmos logs what happened so the team can audit and monitor the outcome."
  }
] as const;

const mcpTools = [
  "cosmos_query",
  "cosmos_recommend_content",
  "cosmos_request_action",
  "cosmos_decide_action",
  "cosmos_list_courses",
  "cosmos_ask_course",
  "cosmos_observability_summary"
] as const;

function IconBubble({ icon, label }: { icon: string; label: string }) {
  return (
    <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-soft bg-white shadow-sm">
      <img src={icon} alt={label} className="h-8 w-8" />
    </span>
  );
}

function ServiceCard({ service }: { service: (typeof services)[number] }) {
  return (
    <Card className="group h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_24px_60px_-36px_rgba(23,15,63,0.75)]">
      <div className="flex items-start gap-4">
        <IconBubble icon={service.icon} label={service.name} />
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--primary-dark)]">
            {service.title}
          </div>
          <h3 className="mt-1 text-lg">{service.name}</h3>
        </div>
      </div>
      <p className="mt-4 text-sm leading-6">{service.role}</p>
      <div className="mt-4 rounded-2xl border border-soft bg-[var(--muted)] p-3 text-sm text-[var(--muted-foreground)]">
        <strong className="text-anchor">Real-world version:</strong> {service.analogy}
      </div>
      <div className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--muted-foreground)]">
        Cosmos uses it for
      </div>
      <p className="mt-1 text-sm">{service.usedFor}</p>
    </Card>
  );
}

function FlowNode({
  title,
  subtitle,
  children
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-3xl border border-soft bg-white/85 p-4 shadow-sm">
      <div className="flex items-center gap-3">
        {children}
        <div>
          <div className="font-bold text-anchor">{title}</div>
          <div className="text-sm text-[var(--muted-foreground)]">{subtitle}</div>
        </div>
      </div>
    </div>
  );
}

function DiagramArrow() {
  return <div className="hidden h-px flex-1 bg-gradient-to-r from-[var(--primary)] to-[var(--ai)] lg:block" />;
}

export function AboutPage() {
  return (
    <div className="page space-y-6">
      <PageHeader
        eyebrow="About Cosmos"
        title="Cosmos under the hood, in normal human language"
        subtitle="Cosmos is the AIHR brain that connects courses, journeys, platform events, approvals, and AI answers. This page shows what happens from the moment someone asks a question to the moment a safe, useful answer comes back."
        actions={
          <div className="inline-list">
            <Badge tone="ai">AI-powered</Badge>
            <Badge tone="success">Human-approved actions</Badge>
            <Badge tone="warning">AWS-backed</Badge>
          </div>
        }
      />

      <section className="grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
        <Card className="overflow-hidden border-0 bg-[linear-gradient(135deg,#170f3f_0%,#31216b_48%,#1ebbf0_130%)] text-white">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-white/80">
                <Sparkles className="h-3.5 w-3.5" />
                What is Cosmos, really?
              </div>
              <h2 className="mt-5 text-3xl text-white md:text-4xl">
                A knowledgeable AIHR colleague that never sleeps.
              </h2>
              <p className="mt-4 max-w-3xl text-base leading-7 text-white/80">
                Cosmos has access to the right learning data, course facts, and operational guardrails. You ask it
                something in everyday language, and it pulls together the right facts before giving you an answer.
              </p>
            </div>
            <div className="grid h-24 w-24 place-items-center rounded-[2rem] bg-white/10 ring-1 ring-white/20">
              <Brain className="h-12 w-12 text-[var(--primary)]" />
            </div>
          </div>
          <div className="mt-8 grid gap-3 md:grid-cols-3">
            {[
              ["Reads the room", "Understands if you want an answer, a recommendation, or an action."],
              ["Checks the facts", "Uses data and documents instead of guessing from memory."],
              ["Keeps guardrails", "Changes that affect people wait for an approved human decision."]
            ].map(([title, text]) => (
              <div key={title} className="rounded-2xl bg-white/10 p-4 ring-1 ring-white/10">
                <div className="font-semibold text-white">{title}</div>
                <p className="mt-2 text-sm leading-6 text-white/75">{text}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-[var(--info-soft)] blur-2xl" />
          <h2>Two doors, one brain</h2>
          <p>
            Cosmos has a friendly workspace for people and an API/MCP surface for tools. Both routes reach the same
            governed runtime.
          </p>
          <div className="mt-5 grid gap-3">
            <div className="rounded-2xl border border-soft bg-[var(--muted)] p-4">
              <div className="flex items-center gap-3">
                <UserRound className="h-5 w-5 text-[var(--primary-dark)]" />
                <strong>People door</strong>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-[var(--muted-foreground)]">
                You <span>{"->"}</span> Browser <span>{"->"}</span> Workspace <span>{"->"}</span> Cosmos
              </div>
            </div>
            <div className="rounded-2xl border border-soft bg-[var(--muted)] p-4">
              <div className="flex items-center gap-3">
                <Bot className="h-5 w-5 text-[var(--ai)]" />
                <strong>Robot door</strong>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-[var(--muted-foreground)]">
                Cursor / Claude <span>{"->"}</span> MCP tools <span>{"->"}</span> Cosmos
              </div>
            </div>
            <div className="rounded-2xl bg-[var(--anchor)] p-4 text-white">
              <div className="flex items-center gap-3">
                <CircleDot className="h-5 w-5 text-[var(--primary)]" />
                <strong>Same Cosmos runtime, same rules, same audit trail.</strong>
              </div>
            </div>
          </div>
        </Card>
      </section>

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="eyebrow">Visual map</div>
            <h2>How a question moves through Cosmos</h2>
            <p className="max-w-3xl">
              Think of this like ordering from a restaurant: you ask for something, the kitchen gathers ingredients,
              the chef prepares the answer, and anything risky waits for a manager's approval.
            </p>
          </div>
          <Badge tone="ai">UI + API + AWS</Badge>
        </div>

        <div className="mt-6 grid gap-3 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:items-center">
          <FlowNode title="You ask" subtitle="Browser workspace or MCP tool">
            <DoorOpen className="h-8 w-8 text-[var(--primary-dark)]" />
          </FlowNode>
          <DiagramArrow />
          <FlowNode title="Cosmos runs" subtitle="API on EKS, protected by IAM">
            <IconBubble icon={eksIcon} label="Amazon EKS" />
          </FlowNode>
          <DiagramArrow />
          <FlowNode title="Facts + AI" subtitle="Athena, S3, RDS, Bedrock">
            <div className="flex -space-x-2">
              <IconBubble icon={athenaIcon} label="Amazon Athena" />
              <IconBubble icon={bedrockIcon} label="Amazon Bedrock" />
            </div>
          </FlowNode>
        </div>

        <div className="mt-8 grid gap-4">
          {questionJourney.map((step, index) => {
            const StepIcon = step.icon;

            return (
              <div
                key={step.title}
                className="grid gap-4 rounded-3xl border border-soft bg-white p-4 md:grid-cols-[auto_1fr_auto] md:items-center"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--accent)] font-bold text-anchor">
                  {index + 1}
                </div>
                <div className="flex items-start gap-3">
                  <StepIcon className="mt-1 h-5 w-5 shrink-0 text-[var(--primary-dark)]" />
                  <div>
                    <h3 className="text-base">{step.title}</h3>
                    <p className="mt-1 text-sm leading-6">{step.text}</p>
                  </div>
                </div>
                {step.icons.length > 0 && (
                  <div className="flex flex-wrap gap-2 md:justify-end">
                    {step.icons.map((icon) => (
                      <IconBubble key={icon} icon={icon} label={step.title} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <section>
        <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="eyebrow">AWS toolkit</div>
            <h2>What each cloud service does for Cosmos</h2>
            <p className="mt-2 max-w-3xl text-[var(--muted-foreground)]">
              These are the real AWS building blocks wired into the project scaffolding and runtime options.
            </p>
          </div>
          <Badge tone="success">Official AWS service icons</Badge>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {services.map((service) => (
            <ServiceCard key={service.name} service={service} />
          ))}
        </div>
      </section>

      <Card className="overflow-hidden">
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <div className="eyebrow">Real-world example</div>
            <h2>Sara just became an HR Director</h2>
            <p>
              Here is how everything ties together when a manager asks Cosmos to build and assign Sara a new learning
              journey.
            </p>
            <div className="mt-6 grid gap-3 rounded-3xl bg-[var(--muted)] p-4">
              <div className="flex items-center gap-3">
                <Route className="h-5 w-5 text-[var(--primary-dark)]" />
                <strong>Goal:</strong>
                <span className="text-sm text-[var(--muted-foreground)]">A relevant, approved journey for Sara.</span>
              </div>
              <div className="flex items-center gap-3">
                <LockKeyhole className="h-5 w-5 text-[var(--primary-dark)]" />
                <strong>Safety:</strong>
                <span className="text-sm text-[var(--muted-foreground)]">No assignment happens without approval.</span>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute left-6 top-6 bottom-6 w-px bg-[var(--soft)]" />
            <div className="grid gap-3">
              {saraWalkthrough.map((item, index) => {
                const ItemIcon = item.icon;

                return (
                  <div key={`${item.service}-${index}`} className="relative grid grid-cols-[3rem_1fr] gap-3">
                    <div className="z-10 flex h-12 w-12 items-center justify-center rounded-2xl border border-soft bg-white shadow-sm">
                      {item.image ? (
                        <img src={item.image} alt={item.service} className="h-8 w-8" />
                      ) : ItemIcon ? (
                        <ItemIcon className="h-6 w-6 text-[var(--primary-dark)]" />
                      ) : null}
                    </div>
                    <div className="rounded-2xl border border-soft bg-white p-4">
                      <div className="text-sm font-bold text-anchor">{item.service}</div>
                      <p className="mt-1 text-sm leading-6">{item.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Card>

      <Card className="border-0 bg-[linear-gradient(135deg,#e9faff_0%,#ffffff_52%,#f0eaff_100%)]">
        <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr] lg:items-center">
          <div>
            <div className="eyebrow">One brain</div>
            <h2>People and agents use the same Cosmos</h2>
            <p>
              The workspace, API, and MCP tools all call the same runtime. That means the answer quality, permissions,
              approval gate, and audit trail stay consistent no matter who is asking.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {mcpTools.map((tool) => (
              <div
                key={tool}
                className="flex items-center gap-3 rounded-2xl border border-soft bg-white/80 p-3 font-mono text-xs text-anchor"
              >
                <Zap className="h-4 w-4 text-[var(--ai)]" />
                {tool}
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        <div className="grid gap-4 md:grid-cols-4">
          {[
            { label: "Front door", value: "API Gateway", icon: Network },
            { label: "Runtime", value: "EKS", icon: Cloud },
            { label: "Data memory", value: "S3 + RDS + DynamoDB", icon: Database },
            { label: "Guardrails", value: "IAM + approvals", icon: Fingerprint }
          ].map((item) => {
            const ItemIcon = item.icon;

            return (
              <div key={item.label} className="rounded-2xl bg-[var(--muted)] p-4">
                <ItemIcon className="h-5 w-5 text-[var(--primary-dark)]" />
                <div className="mt-3 text-xs font-bold uppercase tracking-[0.16em] text-[var(--muted-foreground)]">
                  {item.label}
                </div>
                <div className="mt-1 font-semibold text-anchor">{item.value}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-5 flex items-center gap-3 rounded-2xl bg-[var(--anchor)] p-4 text-white">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-[var(--primary)]" />
          <p className="m-0 text-sm text-white/85">
            In short: Cosmos takes a plain question, finds the right AIHR facts, asks the right AI model, and only
            changes real systems after a person approves the action.
          </p>
        </div>
      </Card>
    </div>
  );
}
